import React, { useCallback, useEffect, useRef, useState } from "react";
import "./style.css";
import { Button, Card, Flex, Modal, Radio, Space } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useLocation } from "react-router";
import { BetaSurveyDismissed, BetaSurveyPopupDismissed } from "@/utils/localStorageWrapper";
import { useFirebaseAuth } from "@/contexts/UserProfileContext/UserProfileContext";
import { useUserOrganization } from "@/utils/partnerData";

function BetaSurvey(props: { onStartSurvey: () => void }) {
    const { onStartSurvey } = props;
    const [userLink, setUserLink] = useState("");
    const [currentStep, setCurrentStep] = useState(0);

    const next = () => {
        setCurrentStep((current) => current + 1);
    };

    const prev = () => {
        setCurrentStep((current) => current - 1);
    };

    const steps = [
        {
            title: "What's your role?",
            content: (
                <Radio.Group
                    value={userLink}
                    onChange={(e) => setUserLink(e.target.value)}
                    options={[
                        {
                            value: "https://form.jotform.com/250505388481157",
                            label: "I am a patient looking for clinical trials",
                        },
                        {
                            value: "https://form.jotform.com/250505245099154",
                            label: "I am a healthcare professional",
                        },
                        {
                            value: "https://form.jotform.com/250505157919056",
                            label: "I am a researcher or study coordinator",
                        },
                    ]}
                />
            ),
        },
        {
            title: "Go to survey",
            content: <p>Please take a minute to answer a few questions and help us improve our service.</p>,
        },
    ];

    return (
        <Flex vertical gap={16}>
            <div>{steps[currentStep].content}</div>
            <Flex justify="flex-end">
                {currentStep < steps.length - 1 && (
                    <Button disabled={userLink === ""} type="primary" onClick={() => next()}>
                        Next
                    </Button>
                )}

                {currentStep > 0 && (
                    <Button style={{ margin: "0 8px" }} onClick={() => prev()}>
                        Previous
                    </Button>
                )}
                {currentStep === steps.length - 1 && (
                    <Button
                        type="primary"
                        href={userLink}
                        rel="noopener noreferrer"
                        target="_blank"
                        onClick={onStartSurvey}
                    >
                        Go to Survey
                    </Button>
                )}
            </Flex>
        </Flex>
    );
}

function BetaSurveyCard() {
    const [isVisible, setIsVisible] = useState(false);
    const firstTime = useRef(true);
    const location = useLocation();

    const handleDismissSurvey = () => {
        setIsVisible(false);
        BetaSurveyDismissed.set("true");
    };

    const handleStartSurvey = () => {
        BetaSurveyPopupDismissed.set("true");
        handleDismissSurvey();
    };

    if (location.pathname === "/results" && firstTime.current) {
        const dismissed = BetaSurveyDismissed.get();
        if (dismissed !== "true") {
            setTimeout(() => {
                setIsVisible(true);
            }, 30000);
        }
        firstTime.current = false;
    }

    if (import.meta.env.VITE_TESTING === "true") {
        return null;
    }

    return (
        <Card
            className="beta-survey-menu slide-in"
            title="Your Input Is Valuable to Us"
            variant="outlined"
            hidden={!isVisible}
            extra={<Button type="text" onClick={handleDismissSurvey} icon={<CloseOutlined />} />}
        >
            <BetaSurvey onStartSurvey={handleStartSurvey} />
        </Card>
    );
}

function BetaSurveyPopup() {
    const [isVisible, setIsVisible] = useState(false);
    const [showSurvey, setShowSurvey] = useState(false);

    // useCallback is required to remove the event listener properly
    const handleMouseLeave = useCallback(() => {
        setIsVisible(true);
    }, []);

    const handleDismissal = () => {
        BetaSurveyPopupDismissed.set("true"); // Prevent popup from reoccurring on page reload
        document.removeEventListener("mouseleave", handleMouseLeave); // Remove the event listener to prevent multiple triggers
        setIsVisible(false);
    };

    useEffect(() => {
        if (BetaSurveyPopupDismissed.get() !== "true") {
            setTimeout(() => {
                document.addEventListener("mouseleave", handleMouseLeave);
            }, 120000);
            return () => document.removeEventListener("mouseleave", handleMouseLeave);
        }

        return () => void 0;
    }, []);

    return (
        <Modal open={isVisible} title="Have a Minute?" onCancel={handleDismissal} footer={null}>
            {!showSurvey && (
                <>
                    <p>Before you go, would you mind taking a quick 2-minute survey to help us improve ResearchLinQ?</p>
                    <Flex justify="end">
                        <Space>
                            <Button onClick={handleDismissal}>Close</Button>
                            <Button onClick={() => setShowSurvey(true)} type="primary">
                                Start Survey
                            </Button>
                        </Space>
                    </Flex>
                </>
            )}
            {showSurvey && <BetaSurvey onStartSurvey={handleDismissal} />}
        </Modal>
    );
}

function CobbSurveyPopup() {
    const { user } = useFirebaseAuth();
    const prefilled = user?.email ? `referredEmail=${user.email}` : "";

    const [isVisible, setIsVisible] = useState(false);

    // useCallback is required to remove the event listener properly
    const handleMouseLeave = useCallback(() => {
        setIsVisible(true);
    }, []);

    const handleDismissal = () => {
        BetaSurveyPopupDismissed.set("true"); // Prevent popup from reoccurring on page reload
        document.removeEventListener("mouseleave", handleMouseLeave); // Remove the event listener to prevent multiple triggers
        setIsVisible(false);
    };

    useEffect(() => {
        if (BetaSurveyPopupDismissed.get() !== "true") {
            setTimeout(() => {
                document.addEventListener("mouseleave", handleMouseLeave);
            }, 120000);
            setTimeout(() => {
                setIsVisible(true);
            }, 240000);

            return () => document.removeEventListener("mouseleave", handleMouseLeave);
        }
        document.removeEventListener("mouseleave", handleMouseLeave);
        return () => document.removeEventListener("mouseleave", handleMouseLeave);
    }, [BetaSurveyPopupDismissed.get()]);

    return (
        <Modal open={isVisible} title="Have a Minute?" onCancel={handleDismissal} footer={null}>
            <p>
                Before you go, would you mind taking a quick survey to help us improve ResearchLinQ? You can always
                access the survey by clicking the Cobb Institute logo in the bottom right.
            </p>
            <Flex justify="end">
                <Space>
                    <Button onClick={handleDismissal}>Not now</Button>
                    <Button
                        type="primary"
                        href={`https://form.jotform.com/250825424296157?${prefilled}`}
                        rel="noopener noreferrer"
                        target="_blank"
                        onClick={handleDismissal}
                    >
                        Take survey
                    </Button>
                </Space>
            </Flex>
        </Modal>
    );
}

function BetaSurveyWrapper() {
    const { userOrganization } = useUserOrganization();

    if (userOrganization.key === "cobb") {
        return <CobbSurveyPopup />;
    }

    return (
        <>
            <BetaSurveyPopup />
            <BetaSurveyCard />
        </>
    );
}

export default BetaSurveyWrapper;
